import { Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { exhaustMap, filter, finalize, Subject, Subscription, tap } from 'rxjs';
import { CiVoteId, CiVoteIdDto, ciVoteOption, IProxyVoteRequest, IVoteRequest } from '../../shared/models/ci/ci-vote';
import { CiBallotService } from '../../shared/services/ci-ballot.service';
import { IBallotCommitteeMemberDto } from '../../shared/models/ballot-base/ballot-committee-member-dto';
import { IBallotVoteDto } from '../../shared/models/ballot-base/ballot-vote-dto';
import { HttpEventType } from '@angular/common/http';
import { IAppUrls } from '../../../../shared/models/app-urls';
import { IUser } from '../../../../shared/models/user';
import { ToastService } from '../../../../shared/services/toast.service';
import { IFileUpload } from '../../../controls/files-upload/files-upload.component';
@Component({
    selector: 'app-cib-place-vote',
    templateUrl: './cib-place-vote.component.html',
    styleUrl: './cib-place-vote.component.scss',
    standalone: false
})


export class CibPlaceVoteComponent {

  @Input() ballotCommitteeMember!: IBallotCommitteeMemberDto;
  @Input() user!: IUser;

  @Input() votes?: IBallotCommitteeMemberDto[] = [];
  @Input() isProxyVote: boolean = false;

  subscriptions: Subscription = new Subscription();
  submit$ = new Subject<void>();
  isSubmitting = false;

  voteOptionForm = new FormGroup({
    agreeSelectCtrl: new FormControl(false),
    disagreeSelectCtrl: new FormControl(false),
    agreeNoteCtrl: new FormControl(''),
    disagreeNoteCtrl: new FormControl(''),
    authorisationDocumentCtrl: new FormControl()
  });

  userAddress!: string;

  committee: IBallotCommitteeMemberDto | undefined;

  isFormValid: boolean = false;

  userVote: IBallotVoteDto | undefined;

  noteTitle = "Please enter a reason for your decision";

  agree = ciVoteOption[CiVoteId.AGREE];
  disagree = ciVoteOption[CiVoteId.DISAGREE];

  appUrls: IAppUrls | null = null;

  committeeMembersLabel: string = 'Committee Members';
  committeeMembersDescription: string = '';

  isSingleProxyVote: boolean = true;

  constructor(private activeModal: NgbActiveModal,
              private _ciBallotService: CiBallotService,
              private _toastService: ToastService)
              {}

  get agreeSelect(){
    return this.voteOptionForm.get('agreeSelectCtrl');
  }
  get disagreeSelect() {
    return this.voteOptionForm.get('disagreeSelectCtrl');
  }

  get agreeNote() {
    return this.voteOptionForm.get('agreeNoteCtrl');
  }
  get disagreeNote() {
    return this.voteOptionForm.get('disagreeNoteCtrl');
  }
  get authorisationDocument():  IFileUpload {
    return (this.voteOptionForm.get('authorisationDocumentCtrl') as FormControl).value;
  }

  ngOnInit(){

    this.loadExistingVote();
    this.setCommitteeMembersDescription(this.votes??[]);
    this.voteOptionForm.valueChanges.subscribe(values => {
      this.validate();
    });

    this.setupSubmitHandler();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadExistingVote() {

    this.userVote = this.ballotCommitteeMember?.votes[0];

    if (!this.userVote) return;

    if (this.userVote?.voteOptionType == CiVoteId.AGREE) {
      this.agreeSelect?.setValue(true);
      this.agreeNote?.setValue(this.userVote?.comments);
    }

    if (this.userVote?.voteOptionType == CiVoteId.DISAGREE) {
      this.disagreeSelect?.setValue(true);
      this.disagreeNote?.setValue(this.userVote?.comments);
    }

  }

  setCommitteeMembersDescription(committeeMembers: IBallotCommitteeMemberDto[]) {
        if (committeeMembers.length > 0) {
          this.committeeMembersLabel = committeeMembers.length > 1 ? 'Committee Members:' : 'Committee Member:';


          let firstCm = committeeMembers[0].fullName;
          let otherCms = '';

          if (committeeMembers.length > 1) {
            otherCms = ` + ${committeeMembers.length - 1} other${committeeMembers.length > 2 ? 's' : ''}`;
          }
          this.committeeMembersDescription = firstCm + otherCms;
        } else {
          this.committeeMembersDescription = '';
        }
  }

  selectAgree() {
    this.reset();
    this.agreeSelect?.setValue(true);
  }

  selectDisagree() {
    this.reset();
    this.disagreeSelect?.setValue(true);
  }

  reset() {
    this.agreeSelect?.setValue(false);
    this.disagreeSelect?.setValue(false);
  }

  close() {
    this.activeModal.close('cancel');
  }

  dismiss(vote: string) {
    this.activeModal.dismiss(vote);
  }

  onSubmitClick(): void {
    this.submit$.next();
  }

  setupSubmitHandler(): void {
    this.subscriptions.add(
      this.submit$
        .pipe(
          filter(() => !this.isSubmitting),
          tap(() => this.isSubmitting = true),
          exhaustMap(() => this.isProxyVote
            ? this._ciBallotService.submitProxyVote(this.getProxyVoteRequest())
            : this._ciBallotService.placeVote(this.getUserVote())),
          finalize(() => this.isSubmitting = false)
        )
        .subscribe({
          next: (response: any) => this.handleSuccess(response),
          error: () => {
            this._toastService.showOnFailedPlacingVote();
            this.close();
          }
        })
    );
  }

  handleSuccess(response: any): void {
    if (this.isProxyVote) {
      if (response.type === HttpEventType.Response) {
        this._toastService.showOnSuccessfulPlacingVote();
         setTimeout(() => {
        this.dismiss(
          this.getProxyVoteRequest().voteOption == CiVoteIdDto.Agree
            ? CiVoteId.AGREE
            : CiVoteId.DISAGREE
        );
      }, 300);
      }
    } else {
      if (response?.success) {
        this._toastService.showOnSuccessfulPlacingVote();
        this.dismiss(this.getUserVote().CiVoteOptionId == CiVoteIdDto.Agree ? CiVoteId.AGREE : CiVoteId.DISAGREE);
      }
    }
  }


  getUserVote() : IVoteRequest {
    return  {
      ballotId: this.ballotCommitteeMember.ballotId,
      committeeMemberIds: [this.ballotCommitteeMember.id],
      CiVoteOptionId : this.agreeSelect?.value  ? CiVoteIdDto.Agree : CiVoteIdDto.Disagree,
      comments: this.agreeSelect?.value ? this.agreeNote?.value??'' : this.disagree ? this.disagreeNote?.value??'' : '',
    }

  }


  getProxyVoteRequest() : IProxyVoteRequest {

    return  {
      ballotId: this.votes ? this.votes[0]?.ballotId : "",
      committeeMemberIds: this.votes?.map(vote => vote.id) ?? [],
      voteOption : this.agreeSelect?.value  ? CiVoteIdDto.Agree : CiVoteIdDto.Disagree,
      comments: this.agreeSelect?.value ? this.agreeNote?.value??'' : this.disagree ? this.disagreeNote?.value??'' : '',
      file: this.authorisationDocument?.files[0]
    }
  }

  validate() {
    this.isFormValid = ((!this.isProxyVote || (this.isProxyVote && this.authorisationDocument != null && this.authorisationDocument.files.length != 0 && !this.authorisationDocument.hasError)) &&
        ((this.agreeSelect?.value) ||
        (this.disagreeSelect?.value && this.disagreeNote?.value != ''))
        )??false;
  }
}
